// Family Tree print / poster-tiling utility. Opens a self-contained print window.
const DPI = 96;
export const PAPER = {
  tabloid: { label: 'Tabloid 11×17', w: 11, h: 17 },
  legal: { label: 'Legal 8.5×14', w: 8.5, h: 14 },
  letter: { label: 'Letter 8.5×11', w: 8.5, h: 11 },
  a4: { label: 'A4 8.27×11.69', w: 8.27, h: 11.69 },
};
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const NW = 184, NH = 64;

// Genogram layout (mirrors FamilyDiagram.js): side-by-side couples, household generations, descend-from edges.
function computeGenogram(people, axis = "portrait") {
  const landscape = axis === "landscape";
  const byId = Object.fromEntries(people.map((p) => [p.id, p]));
  const spouseIdsOf = (p) => (Array.isArray(p.spouse_ids) && p.spouse_ids.length ? p.spouse_ids.filter((x) => byId[x]) : (p.spouse_id && byId[p.spouse_id] ? [p.spouse_id] : []));
  const descParentsOf = (c) => { const df = c.descend_from || "both"; if (df === "parent") return [c.parent_id]; if (df === "parent2") return [c.parent_id2]; return [c.parent_id, c.parent_id2]; };
  const dC = {};
  const ownDepth = (id, g = 0) => { if (dC[id] != null) return dC[id]; const p = byId[id]; if (!p || g > 80) return 0; const ps = [p.parent_id, p.parent_id2].filter((x) => x && byId[x]); const d = ps.length ? Math.min(...ps.map((x) => ownDepth(x, g + 1))) + 1 : 0; dC[id] = d; return d; };
  const groupId = {}, groups = {}, seen = new Set();
  people.forEach((p) => {
    if (seen.has(p.id)) return;
    const comp = [], stack = [p.id], local = new Set();
    while (stack.length) { const id = stack.pop(); if (local.has(id)) continue; local.add(id); seen.add(id); comp.push(id); spouseIdsOf(byId[id]).forEach((sd) => { if (byId[sd] && !local.has(sd)) stack.push(sd); }); }
    const hasPar = (id) => { const q = byId[id]; return [q.parent_id, q.parent_id2].some((x) => x && byId[x]); };
    const head = comp.slice().sort((a, b) => (hasPar(b) ? 1 : 0) - (hasPar(a) ? 1 : 0) || ((byId[b].sibling_ids || []).length ? 1 : 0) - ((byId[a].sibling_ids || []).length ? 1 : 0) || ownDepth(a) - ownDepth(b) || spouseIdsOf(byId[b]).length - spouseIdsOf(byId[a]).length || (a < b ? -1 : 1))[0];
    const headSpouses = spouseIdsOf(byId[head]).filter((x) => comp.includes(x));
    const rest = comp.filter((x) => x !== head && !headSpouses.includes(x));
    const half = Math.floor(headSpouses.length / 2);
    const ordered = [...headSpouses.slice(0, half), head, ...headSpouses.slice(half), ...rest]; ordered.forEach((id) => (groupId[id] = head)); groups[head] = ordered;
  });
  const gC = {};
  const gGen = (H, guard = 0) => { if (gC[H] != null) return gC[H]; if (guard > 120) return 0; const members = groups[H]; const pg = new Set(); members.forEach((mid) => { const p = byId[mid]; [p.parent_id, p.parent_id2].forEach((pp) => { if (pp && byId[pp] && groupId[pp] !== H) pg.add(groupId[pp]); }); }); const g = pg.size ? Math.max(...[...pg].map((x) => gGen(x, guard + 1) + 1)) : 0; gC[H] = g; return g; };
  const childGroupsOf = (H) => { const members = new Set(groups[H]); const res = new Set(); people.forEach((c) => { const dps = descParentsOf(c).filter((x) => x && byId[x]); if (dps.some((pp) => members.has(pp))) { const g = groupId[c.id]; if (g !== H) res.add(g); } }); return [...res]; };
  const siblingIdsOf = (p) => (Array.isArray(p.sibling_ids) ? p.sibling_ids.filter((x) => byId[x]) : []);
  const sibAdj = {}; Object.keys(groups).forEach((h) => (sibAdj[h] = new Set()));
  people.forEach((p) => siblingIdsOf(p).forEach((sid) => { const a = groupId[p.id], b = groupId[sid]; if (a && b && a !== b) { sibAdj[a].add(b); sibAdj[b].add(a); } }));
  const clusterId = {}, clusterMembers = {}; let ciN = 0;
  Object.keys(groups).forEach((h) => { if (clusterId[h] != null) return; const id = ciN++; clusterMembers[id] = []; const st = [h]; while (st.length) { const x = st.pop(); if (clusterId[x] != null) continue; clusterId[x] = id; clusterMembers[id].push(x); [...(sibAdj[x] || [])].forEach((y) => { if (clusterId[y] == null) st.push(y); }); } });
  const clusterGen = {}; Object.keys(clusterMembers).forEach((id) => { clusterGen[id] = Math.max(...clusterMembers[id].map((h) => gGen(h))); });
  const genOf = (H) => clusterGen[clusterId[H]] ?? gGen(H);
  const col = {}, gen = {}, placed = new Set();
  const layoutGroup = (H, leftX) => {
    if (placed.has(H)) return 0; placed.add(H);
    const members = groups[H];
    const kids = childGroupsOf(H).filter((g) => !placed.has(g)).sort((a, b) => groups[a].length - groups[b].length);
    let cl = leftX, cw = 0; kids.forEach((g) => { const w = layoutGroup(g, cl); cl += w; cw += w; });
    const width = Math.max(members.length, cw); const center = leftX + width / 2; const d = genOf(H);
    const start = center - members.length / 2; members.forEach((mid, i) => { col[mid] = start + i + 0.5; gen[mid] = d; });
    return width;
  };
  const rootHeads = Object.keys(groups).filter((H) => !groups[H].some((mid) => { const p = byId[mid]; return [p.parent_id, p.parent_id2].some((pp) => pp && byId[pp] && groupId[pp] !== H); }));
  const rootSet = new Set(rootHeads);
  let cursor = 0;
  const doneCluster = new Set();
  rootHeads.forEach((H) => { const cid = clusterId[H]; if (doneCluster.has(cid)) return; doneCluster.add(cid); const clusterRoots = clusterMembers[cid].filter((h) => rootSet.has(h)).sort((a, b) => groups[a].length - groups[b].length); clusterRoots.forEach((h) => { if (placed.has(h)) return; const w = layoutGroup(h, cursor); cursor += Math.max(1, w) + 1; }); });
  Object.keys(groups).forEach((H) => { if (!placed.has(H)) { const w = layoutGroup(H, cursor); cursor += Math.max(1, w) + 1; } });
  const CELL = 210, GEN = 150;
  const nodes = people.map((p) => {
    const c = col[p.id] ?? 0, g = gen[p.id] ?? 0;
    let x, y; if (landscape) { x = g * (GEN + 80); y = c * CELL; } else { x = c * CELL; y = g * GEN; }
    if (p.x != null) x = p.x; if (p.y != null) y = p.y;
    return { id: p.id, x, y, color: p.color || '#0F62FE', photo: p.photo || '', photo_rot: p.photo_rot || 0,
      name: [p.first, p.middle, p.last].filter(Boolean).join(' ') || 'Unnamed', role: p.branch || '', dob: p.dob || '', dod: p.dod || '' };
  });
  const partnersOf = (p) => { const cur = spouseIdsOf(p); const fmr = Array.isArray(p.former_spouse_ids) ? p.former_spouse_ids.filter((x) => byId[x]) : []; return [...new Set([...cur, ...fmr])]; };
  const areCouple = (a, b) => !!(a && b && byId[a] && byId[b] && (partnersOf(byId[a]).includes(b) || partnersOf(byId[b]).includes(a)));
  const posById = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const unions = {};
  people.forEach((c) => {
    if ((c.descend_from || 'both') !== 'both') return;
    const a = c.parent_id, b = c.parent_id2;
    if (!(a && b && byId[a] && byId[b] && areCouple(a, b))) return;
    const key = [a, b].sort().join('|');
    if (!unions[key]) unions[key] = { a, b, children: [] };
    unions[key].children.push(c.id);
  });
  const unionChildIds = new Set(); Object.values(unions).forEach((u) => u.children.forEach((id) => unionChildIds.add(id)));
  const parentEdges = []; people.forEach((c) => { if (unionChildIds.has(c.id)) return; descParentsOf(c).forEach((pid) => { if (pid && byId[pid]) parentEdges.push({ from: pid, to: c.id, color: byId[pid].color || '#888' }); }); });
  const unionEdges = [];
  Object.values(unions).forEach((u) => {
    const na = posById[u.a], nb = posById[u.b]; if (!na || !nb) return;
    let ux, uy;
    if (landscape) { ux = Math.max(na.x, nb.x) + NW; uy = (na.y + nb.y) / 2 + NH / 2; }
    else { ux = (na.x + nb.x) / 2 + NW / 2; uy = Math.min(na.y, nb.y) + NH / 2; }
    u.children.forEach((cid) => { if (posById[cid]) unionEdges.push({ ux, uy, to: cid, color: byId[u.a].color || '#888' }); });
  });
  const spouseEdges = []; const spKey = new Set();
  people.forEach((p) => { spouseIdsOf(p).forEach((sid) => { if (!byId[sid]) return; const k = [p.id, sid].sort().join('-'); if (spKey.has(k)) return; spKey.add(k); spouseEdges.push({ a: p.id, b: sid }); }); });
  return { nodes, parentEdges, unionEdges, spouseEdges, landscape };
}

const lum = (hex) => { const h = String(hex || '').replace('#', ''); if (h.length < 6) return 0; return (0.299 * parseInt(h.slice(0, 2), 16) + 0.587 * parseInt(h.slice(2, 4), 16) + 0.114 * parseInt(h.slice(4, 6), 16)) / 255; };

function diagramInnerHtml(nodes, parentEdges, unionEdges, spouseEdges, scaledW, scaledH, s, minX, minY, ox, oy, landscape) {
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const L = (n) => ox + (n.x - minX) * s, T = (n) => oy + (n.y - minY) * s;
  const nodeHtml = nodes.map((n) => {
    const left = L(n), top = T(n);
    const fg = lum(n.color) > 0.62 ? '#0a1a3a' : '#ffffff';
    const ta = n.photo ? 'text-align:center;' : '';
    const nw = 'overflow-wrap:break-word;word-break:normal;';
    const ph = n.photo ? `<div style="margin:0 auto ${5 * s}px;width:${40 * s}px;height:${40 * s}px;border-radius:50%;overflow:hidden;border:${2 * s}px solid rgba(255,255,255,.7)">
      <img src="${n.photo}" style="width:100%;height:100%;object-fit:cover;transform:rotate(${n.photo_rot || 0}deg)"/></div>` : '';
    return `<div style="position:absolute;left:${left}px;top:${top}px;width:${NW * s}px;min-height:${(NH + (n.photo ? 44 : 0)) * s}px;
      background:${n.color};color:${fg};border-radius:${6 * s}px;box-shadow:0 2px 5px rgba(0,0,0,.25);
      padding:${7 * s}px ${11 * s}px;font-size:${11 * s}px;line-height:1.2;box-sizing:border-box;overflow:hidden;">
      ${ph}
      <div>
        <div style="${ta}${nw}font-weight:800;text-transform:uppercase;letter-spacing:.4px;font-size:${13 * s}px">${esc(n.name)}</div>
        ${n.role ? `<div style="${ta}opacity:.92;font-weight:500;font-size:${10.5 * s}px">${esc(n.role)}</div>` : ''}
        ${(n.dob || n.dod) ? `<div style="${ta}opacity:.78;font-size:${9.5 * s}px">${n.dob ? `b.${esc(n.dob)}` : ''}${n.dob && n.dod ? ' · ' : ''}${n.dod ? `d.${esc(n.dod)}` : ''}</div>` : ''}
      </div>
    </div>`;
  }).join('');
  const paths = [];
  parentEdges.forEach((e) => {
    const p = byId[e.from], c = byId[e.to]; if (!p || !c) return;
    const w = NW * s, h = NH * s;
    let d;
    if (landscape) { const sx = L(p) + w, sy = T(p) + h / 2, tx = L(c), ty = T(c) + h / 2, mid = (sx + tx) / 2; d = `M${sx},${sy} L${mid},${sy} L${mid},${ty} L${tx},${ty}`; }
    else { const sx = L(p) + w / 2, sy = T(p) + h, tx = L(c) + w / 2, ty = T(c), mid = (sy + ty) / 2; d = `M${sx},${sy} L${sx},${mid} L${tx},${mid} L${tx},${ty}`; }
    paths.push(`<path d="${d}" stroke="${e.color}" stroke-width="${2 * s}" fill="none" opacity=".85"/>`);
  });
  (unionEdges || []).forEach((e) => {
    const c = byId[e.to]; if (!c) return;
    const w = NW * s, h = NH * s;
    const sx = ox + (e.ux - minX) * s, sy = oy + (e.uy - minY) * s;
    let d;
    if (landscape) { const tx = L(c), ty = T(c) + h / 2, mid = (sx + tx) / 2; d = `M${sx},${sy} L${mid},${sy} L${mid},${ty} L${tx},${ty}`; }
    else { const tx = L(c) + w / 2, ty = T(c), mid = (sy + ty) / 2; d = `M${sx},${sy} L${sx},${mid} L${tx},${mid} L${tx},${ty}`; }
    paths.push(`<path d="${d}" stroke="${e.color}" stroke-width="${2 * s}" fill="none" opacity=".85"/>`);
  });
  spouseEdges.forEach((e) => {
    let a = byId[e.a], b = byId[e.b]; if (!a || !b) return;
    const w = NW * s, h = NH * s;
    let d;
    if (landscape) { if (a.y > b.y) [a, b] = [b, a]; const x = L(a) + w / 2; d = `M${x},${T(a) + h} L${x},${T(b)}`; }
    else { if (a.x > b.x) [a, b] = [b, a]; const y = T(a) + h / 2; d = `M${L(a) + w},${y} L${L(b)},${y}`; }
    paths.push(`<path d="${d}" stroke="#D02670" stroke-width="${2 * s}" fill="none"/>`);
  });
  return `<svg style="position:absolute;left:0;top:0" width="${scaledW}" height="${scaledH}">${paths.join('')}</svg>${nodeHtml}`;
}

export function printFamilyTree(opts) {
  const { tree, people, groups = null, mode = 'diagram', layout = 'single', cols = 2, rows = 2,
    paperKey = 'letter', orientation = 'portrait', branding = true, axis = 'portrait' } = opts;
  // GENERIC (org) trees print as a department-grouped, salary-ranked table matching
  // the on-screen org table. Flows naturally across pages.
  if (groups) { printOrgTable({ tree, groups, paperKey, orientation, branding }); return; }
  const paper = PAPER[paperKey] || PAPER.letter;
  const [pwIn, phIn] = orientation === 'landscape' ? [paper.h, paper.w] : [paper.w, paper.h];
  const pageW = Math.round(pwIn * DPI), pageH = Math.round(phIn * DPI);
  const M = 24, headerH = branding ? 46 : 0, footerH = branding ? 28 : 0;
  const contentW = pageW - 2 * M, contentH = pageH - headerH - footerH - 2 * M;
  const title = esc(tree?.name || 'Family Tree');
  const dateStr = new Date().toLocaleDateString();
  const win = window.open('', '_blank');
  if (!win) { alert('Please allow pop-ups to print.'); return; }

  const header = branding ? `<div class="hdr"><span class="wm">Familigi™</span><span class="ttl">${title}</span></div>` : '';
  const footer = (panelLabel) => branding ? `<div class="ftr"><span>Full Option Family Office</span><span>${dateStr}${panelLabel ? ' · ' + panelLabel : ''}</span></div>` : '';

  let body = '';
  if (mode === 'cards') {
    const cards = people.map((p) => `<div class="card" style="border-left:5px solid ${p.color || '#0F62FE'}">
      ${p.photo ? `<div class="cimg"><img src="${p.photo}" style="transform:rotate(${p.photo_rot || 0}deg)"/></div>` : ''}
      <div class="cbody">
        <div class="cn">${esc([p.first, p.middle, p.last].filter(Boolean).join(' ') || 'Unnamed')}</div>
        ${p.attrs?.maiden ? `<div class="cm">née ${esc(p.attrs.maiden)}</div>` : ''}
        <div class="cd">${p.branch ? esc(p.branch) : ''}${p.dob ? ` · b. ${esc(p.dob)}` : ''}${p.dod ? ` · d. ${esc(p.dod)}` : ''}</div>
        ${p.attrs?.spouse_first ? `<div class="cd">⚭ ${esc([p.attrs.spouse_first, p.attrs.spouse_middle, p.attrs.spouse_last].filter(Boolean).join(' '))}</div>` : ''}
      </div>
    </div>`).join('');
    body = `<div class="page"><div class="pagebody cards">${cards || '<p>No records.</p>'}</div>${header}${footer('')}</div>`;
  } else {
    const { nodes, parentEdges, unionEdges, spouseEdges, landscape } = computeGenogram(people, axis);
    if (!nodes.length) { body = `<div class="page"><div class="pagebody"><p>No records to diagram.</p></div>${header}${footer('')}</div>`; }
    else {
      const xs = nodes.map((n) => n.x), ys = nodes.map((n) => n.y);
      const minX = Math.min(...xs), minY = Math.min(...ys);
      const cW = (Math.max(...xs) - minX) + NW + 20, cH = (Math.max(...ys) - minY) + NH + 20;
      const gc = layout === 'panels' ? Math.max(1, cols) : 1, gr = layout === 'panels' ? Math.max(1, rows) : 1;
      const bigW = gc * contentW, bigH = gr * contentH;
      const s = Math.min(bigW / cW, bigH / cH);
      const scaledW = cW * s, scaledH = cH * s;
      const ox = (bigW - scaledW) / 2, oy = (bigH - scaledH) / 2;
      const inner = diagramInnerHtml(nodes, parentEdges, unionEdges, spouseEdges, bigW, bigH, s, minX, minY, ox, oy, landscape);
      const pages = [];
      for (let r = 0; r < gr; r++) for (let c = 0; c < gc; c++) {
        const label = gc * gr > 1 ? `Panel R${r + 1}·C${c + 1} of ${gr}×${gc}` : '';
        pages.push(`<div class="page"><div class="pagebody"><div class="vp">
          <div class="big" style="width:${bigW}px;height:${bigH}px;transform:translate(${-c * contentW}px,${-r * contentH}px)">${inner}</div>
        </div></div>${header}${footer(label)}</div>`);
      }
      body = pages.join('');
    }
  }

  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>
  <style>
    @page { size: ${pwIn}in ${phIn}in; margin: 0; }
    * { box-sizing: border-box; }
    body { margin: 0; font-family: -apple-system, Segoe UI, Roboto, sans-serif; color: #161616; }
    .page { position: relative; width: ${pageW}px; height: ${pageH}px; overflow: hidden; page-break-after: always; }
    .page:last-child { page-break-after: auto; }
    .pagebody { position: absolute; left: ${M}px; top: ${M + headerH}px; width: ${contentW}px; height: ${contentH}px; overflow: hidden; }
    .vp { width: ${contentW}px; height: ${contentH}px; overflow: hidden; position: relative; }
    .big { position: absolute; left: 0; top: 0; }
    .hdr { position: absolute; left: ${M}px; right: ${M}px; top: ${M / 2}px; height: ${headerH}px; display: flex; align-items: center; gap: 12px; border-bottom: 2px solid #0F62FE; }
    .hdr .wm { font-weight: 800; color: #0F62FE; font-size: 18px; letter-spacing: .5px; }
    .hdr .ttl { font-size: 15px; opacity: .8; }
    .ftr { position: absolute; left: ${M}px; right: ${M}px; bottom: ${M / 3}px; height: ${footerH}px; display: flex; justify-content: space-between; align-items: center; font-size: 10px; opacity: .55; border-top: 1px solid #ccc; }
    .cards { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; align-content: start; }
    .card { border: 1px solid #e0e0e0; border-radius: 8px; padding: 8px 12px; background: #fff; break-inside: avoid; display: flex; gap: 10px; align-items: flex-start; }
    .cimg { width: 44px; height: 44px; flex: 0 0 auto; border-radius: 50%; overflow: hidden; border: 1px solid rgba(0,0,0,.15); }
    .cimg img { width: 100%; height: 100%; object-fit: cover; }
    .cn { font-weight: 600; font-size: 13px; } .cm { opacity: .6; font-size: 11px; } .cd { opacity: .7; font-size: 11px; margin-top: 2px; }
    @media screen { body { background: #eee; padding: 16px; } .page { margin: 0 auto 16px; box-shadow: 0 2px 12px rgba(0,0,0,.2); background:#fff; } }
    @media print { body { background: #fff; padding: 0; } }
  </style></head><body>${body}
  <script>window.onload=function(){setTimeout(function(){window.print();},350);};</script>
  </body></html>`);
  win.document.close();
  win.focus();
}


// Department-grouped, salary-ranked org table print (GENERIC trees).
function printOrgTable({ tree, groups, paperKey = 'letter', orientation = 'portrait', branding = true }) {
  const paper = PAPER[paperKey] || PAPER.letter;
  const [pwIn, phIn] = orientation === 'landscape' ? [paper.h, paper.w] : [paper.w, paper.h];
  const title = esc(tree?.name || 'Organisation');
  const dateStr = new Date().toLocaleDateString();
  const win = window.open('', '_blank');
  if (!win) { alert('Please allow pop-ups to print.'); return; }

  const personRow = (p, ix) => {
    const g = Number(p.attrs?.salary_grade || 0);
    const parts = [p.attrs?.title, p.branch].filter(Boolean).map(esc).join(' · ');
    return `<div class="prow" style="border-left:4px solid ${p.color || '#0F62FE'}">
      <span class="pn">${ix + 1}</span>
      <span class="pname">${esc([p.first, p.middle, p.last].filter(Boolean).join(' ') || 'Unnamed')}${parts ? `<span class="pmeta">${parts}</span>` : ''}</span>
      ${g > 0 ? `<span class="pgr">G${g}</span>` : ''}
    </div>`;
  };
  const sections = groups.map((grp) => `<section class="grp">
      <div class="grphd"><span class="grrk">${grp.rank ? esc(grp.rank) : '–'}</span><span class="grnm">${esc(grp.name)}</span><span class="grct">${grp.members.length} ${grp.members.length === 1 ? 'person' : 'people'}</span></div>
      ${grp.members.map(personRow).join('')}
    </section>`).join('') || '<p>No records to print.</p>';

  const header = branding ? `<div class="hdr"><span class="wm">Familigi™</span><span class="ttl">${title}</span><span class="dt">${dateStr}</span></div>` : `<div class="hdr"><span class="ttl">${title}</span></div>`;

  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>
  <style>
    @page { size: ${pwIn}in ${phIn}in; margin: 0.5in; }
    * { box-sizing: border-box; }
    body { margin: 0; font-family: -apple-system, Segoe UI, Roboto, sans-serif; color: #161616; }
    .hdr { display: flex; align-items: baseline; gap: 12px; border-bottom: 2px solid #0F62FE; padding-bottom: 6px; margin-bottom: 12px; }
    .hdr .wm { font-weight: 800; color: #0F62FE; font-size: 18px; letter-spacing: .5px; }
    .hdr .ttl { font-size: 15px; opacity: .8; } .hdr .dt { margin-left: auto; font-size: 11px; opacity: .55; }
    .grp { border: 1px solid #c9d6f5; border-radius: 8px; margin-bottom: 12px; overflow: hidden; break-inside: avoid; }
    .grphd { display: flex; align-items: center; gap: 8px; background: #eef3fe; border-bottom: 1px solid #c9d6f5; padding: 5px 10px; }
    .grrk { display: inline-flex; width: 20px; height: 20px; align-items: center; justify-content: center; border-radius: 4px; background: #0F62FE; color: #fff; font-weight: 800; font-size: 12px; }
    .grnm { font-weight: 700; font-size: 13px; } .grct { margin-left: auto; font-size: 11px; opacity: .6; }
    .prow { display: flex; align-items: center; gap: 10px; padding: 6px 10px; border-top: 1px solid #f0f0f0; break-inside: avoid; }
    .prow:first-of-type { border-top: none; }
    .pn { width: 22px; text-align: center; font-weight: 800; color: #0F62FE; font-size: 13px; }
    .pname { flex: 1; font-weight: 600; font-size: 13px; } .pmeta { font-weight: 400; opacity: .6; font-size: 11px; margin-left: 8px; }
    .pgr { font-size: 11px; opacity: .7; font-weight: 600; }
    @media screen { body { background: #eee; padding: 24px; } .doc { max-width: 800px; margin: 0 auto; background: #fff; padding: 28px; box-shadow: 0 2px 12px rgba(0,0,0,.2); } }
  </style></head><body><div class="doc">${header}${sections}</div>
  <script>window.onload=function(){setTimeout(function(){window.print();},350);};</script>
  </body></html>`);
  win.document.close();
  win.focus();
}
